import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, Trash2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = ["apartment", "house", "studio", "commercial", "land"];

export default function Properties() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const { data: items = [] } = useQuery({
    queryKey: ["properties"],
    queryFn: async () => (await api.get("/properties")).data,
  });
  const { data: regions = [] } = useQuery({
    queryKey: ["market-regions"], queryFn: async () => (await api.get("/market/regions")).data.regions,
  });

  const create = useMutation({
    mutationFn: (payload) => api.post("/properties", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      setForm({});
      toast.success("OK");
    },
    onError: () => toast.error("Error"),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/properties/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const submit = () => {
    if (!form.name) return toast.error(t("name"));
    create.mutate({
      name: form.name,
      address: form.address || "",
      property_type: form.property_type || "apartment",
      area: parseFloat(form.area) || 0,
      rooms: parseInt(form.rooms) || 0,
      purchase_price: parseFloat(form.purchase_price) || 0,
      current_value: parseFloat(form.current_value) || parseFloat(form.purchase_price) || 0,
      region: form.region || null,
    });
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">{t("nav_properties")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-property-btn" className="rounded-sm gap-2">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">{t("add_property")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display text-2xl">{t("add_property")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2 space-y-2">
                <Label>{t("name")}</Label>
                <Input data-testid="prop-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{t("address")}</Label>
                <Input data-testid="prop-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>{t("type")}</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger data-testid="prop-type" className="rounded-sm"><SelectValue placeholder="apartment" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {TYPES.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("area")}</Label>
                <Input type="number" data-testid="prop-area" value={form.area || ""} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>{t("rooms")}</Label>
                <Input type="number" data-testid="prop-rooms" value={form.rooms || ""} onChange={(e) => setForm({ ...form, rooms: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>{t("purchase_price")}</Label>
                <Input type="number" data-testid="prop-price" value={form.purchase_price || ""} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-sm" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{t("current_value")}</Label>
                <Input type="number" data-testid="prop-current" value={form.current_value || ""} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="rounded-sm" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{t("region")}</Label>
                <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                  <SelectTrigger data-testid="prop-region" className="rounded-sm"><SelectValue placeholder={t("region_placeholder")} /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("cancel")}</Button>
              <Button onClick={submit} data-testid="prop-save" disabled={create.isPending} className="rounded-sm">{t("save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_properties")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card key={p.id} data-testid={`property-card-${p.id}`} onClick={() => navigate(`/properties/${p.id}`)} className="p-6 rounded-sm border-border hover:-translate-y-1 transition-transform duration-300 group cursor-pointer">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center">
                  <Building2 className="h-5 w-5" />
                </div>
                <Button variant="ghost" size="icon" data-testid={`del-property-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); remove.mutate(p.id); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-[hsl(var(--destructive))]">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <h3 className="font-display text-xl mt-4">{p.name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <MapPin className="h-3 w-3" /> {p.address || "—"}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Badge variant="secondary" className="rounded-sm">{p.property_type}</Badge>
                {p.area > 0 && <Badge variant="outline" className="rounded-sm">{p.area} m²</Badge>}
                {p.rooms > 0 && <Badge variant="outline" className="rounded-sm">{p.rooms} T</Badge>}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
                <span className="text-xs uppercase text-muted-foreground">{t("current_value")}</span>
                <span className="font-display text-2xl tabular-nums">{fmtCurrency(p.current_value || p.purchase_price)}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
